import { NavigationProp, useNavigation } from "@react-navigation/native";
import { Text } from "@ui-kitten/components";
import GernericEventBackground from "components/GernericEventBackground";
import WhiteText from "components/WhiteText";
import { Image } from "expo-image";
import { AccountTicketDetails } from "features/types";
import { useAppSelector } from "hooks/useAppSelector";
import useMarketingAssetsSections from "hooks/useMarketingAssetsSections";
import { MainFlowParamList } from "navigation/types";
import i18n from "plugins/localization";
import React, { useMemo } from "react";
import { Pressable, StyleSheet, View } from "react-native";
import { formatEventDate, isSportsEvent } from "utils/eventUtils";
import { svgURL } from "utils/fileUtils";
const LOGO_SIZE = 125;

const MyReservations = ({
  accountEvents,
}: {
  accountEvents: AccountTicketDetails[];
}) => {
  const { tenantThemeColor, tenantSecondaryColor } = useAppSelector(
    (state) => state.tenant
  );
  const earliestDate = accountEvents[0].event.date;
  const upcomingEvent =
    accountEvents.find(
      (e) => e.event.date === earliestDate && e.event.type !== "PARKING"
    ) ?? accountEvents[0];
  const navigation = useNavigation<NavigationProp<MainFlowParamList>>();
  const eventType = upcomingEvent.event.type;

  const { isSectionMarketingAssets } = useMarketingAssetsSections(
    upcomingEvent.tickets[0].planPackageId
  );

  const shouldShowNumberOfSeats = useMemo(() => {
    if (upcomingEvent.premiumClass === null) return true;
    if (upcomingEvent.event.type === "EXPERIENCE") return false;
    if (isSectionMarketingAssets(upcomingEvent.tickets[0].seat.section))
      return false;
    return true;
  }, [upcomingEvent]);

  const upcomingEventTickets = upcomingEvent.groupCode.tickets.filter((t) =>
    ["RESERVED", "ISSUED", "SCANNED"].includes(t.status)
  );

  const nonSportsEvent = !isSportsEvent(eventType);

  return (
    <Pressable
      onPress={() => {
        navigation.navigate("MyEvents", {
          screen: "EventDetail",
          params: {
            groupCode: upcomingEvent.groupCode.groupCode,
          },
        });
      }}
    >
      <View style={styles.bannerContainer}>
        <View
          style={[
            styles.leftContainer,
            { backgroundColor: tenantSecondaryColor },
          ]}
        >
          {nonSportsEvent ? (
            <WhiteText category="s2" style={{ textAlign: "center" }}>
              {upcomingEvent.event.name}
            </WhiteText>
          ) : (
            <WhiteText category="s2" style={{ textAlign: "center" }}>
              My Next Game
            </WhiteText>
          )}
          {eventType !== "EXPERIENCE" && (
            <>
              {upcomingEvent.event.containsTime && (
                <WhiteText
                  category="s2"
                  status="control"
                  style={{ marginVertical: 4 }}
                >
                  {formatEventDate(upcomingEvent.event, "hh:mm A")}
                </WhiteText>
              )}
              <WhiteText status="control" category="s2">
                {formatEventDate(upcomingEvent.event, "ddd, MMM DD")}
              </WhiteText>
            </>
          )}
          {shouldShowNumberOfSeats && (
            <WhiteText category="h6" style={{ marginTop: 4 }}>
              {`${upcomingEventTickets.length} ${i18n.t("common.seat", {
                count: upcomingEventTickets.length,
              })}`}
            </WhiteText>
          )}
        </View>
        <View
          style={[styles.rightContainer, { backgroundColor: tenantThemeColor }]}
        >
          {nonSportsEvent ? (
            <>
              {upcomingEvent.event.imageUrl ? (
                <Image
                  source={{ uri: upcomingEvent.event.imageUrl || undefined }}
                  style={{ height: "100%", width: "100%", minHeight: 150 }}
                  contentFit="fill"
                ></Image>
              ) : (
                <GernericEventBackground showLogo={false} />
              )}
            </>
          ) : (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                overflow: "hidden",
              }}
            >
              <>
                <View style={styles.tenantLogo}>
                  <Image
                    source={{
                      uri: svgURL(upcomingEvent.event.hostName),
                    }}
                    style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                    responsivePolicy="initial"
                    contentFit="contain"
                  ></Image>
                </View>
                <View>
                  <View style={styles.topLine} />
                  <Text
                    style={[
                      styles.vsText,
                      {
                        color: tenantSecondaryColor,
                      },
                    ]}
                  >
                    VS
                  </Text>
                  <View style={styles.bottomLine} />
                </View>
              </>
              <View style={styles.opponentLogo}>
                <Image
                  source={{
                    uri: svgURL(upcomingEvent.event.opponentName),
                  }}
                  style={{ width: LOGO_SIZE, height: LOGO_SIZE }}
                  responsivePolicy="initial"
                  contentFit="contain"
                ></Image>
              </View>
            </View>
          )}
        </View>
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  bannerContainer: {
    flexDirection: "row",
    borderRadius: 15,
    overflow: "hidden",
  },
  leftContainer: {
    flex: 0.32,
    paddingHorizontal: 10,
    paddingVertical: 25,
    justifyContent: "center",
    alignItems: "center",
  },
  rightContainer: {
    flex: 0.68,
  },
  tenantLogo: {
    opacity: 0.5,
    transform: [
      {
        translateX: 10,
      },
      {
        translateY: -20,
      },
    ],
  },
  opponentLogo: {
    zIndex: -1,
    opacity: 0.5,
    transform: [
      {
        translateX: 0,
      },
      {
        translateY: 40,
      },
    ],
  },
  topLine: {
    position: "absolute",
    right: -12,
    top: -22,
    width: 40,
    height: 2,
    backgroundColor: "white",
    transform: [{ rotate: "-75deg" }],
    opacity: 0.5,
  },
  bottomLine: {
    position: "absolute",
    right: 2,
    bottom: -23,
    width: 40,
    height: 2,
    backgroundColor: "white",
    transform: [{ rotate: "-75deg" }],
    opacity: 0.5,
  },
  vsText: {
    fontSize: 24,
    fontWeight: "bold",
  },
  numberOfSeatsChip: {
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
    bottom: 10,
    right: 10,
  },
});

export default MyReservations;
